import { memo, useState } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tag } from '@carbon/react';
import { FlowData } from '@carbon/icons-react';
import type { ExplorationGraphNode } from '@snxt/snxt-onto-share';

export interface ConceptInstanceNodeData
  extends ExplorationGraphNode,
    Record<string, unknown> {}

/**
 * Custom React Flow node for A-Box concept instances (layer = 'instance').
 *
 * Visually distinct from ExplorerConceptNode:
 *  - Teal accent color (vs. blue for T-Box concepts)
 *  - FlowData icon to indicate "extracted data"
 *  - Shows instance label + attribute count badge
 *  - Smaller and more compact than concept nodes
 */
function ConceptInstanceNodeInner({ data, selected }: NodeProps) {
  const [isHovered, setIsHovered] = useState(false);
  const d = data as ConceptInstanceNodeData;
  const inst = d.instance;
  const attrCount = inst ? Object.keys(inst.attributes ?? {}).length : 0;
  const confidence =
    inst?.extraction_confidence != null
      ? Math.round(inst.extraction_confidence * 100)
      : null;

  const borderColor = selected
    ? 'var(--cds-focus)'
    : 'var(--cds-support-teal-60, #007d79)';

  const boxShadow = selected
    ? '0 0 0 2px var(--cds-focus)'
    : isHovered
      ? '0 2px 8px rgba(0,125,121,0.25)'
      : '0 2px 6px rgba(0,0,0,0.10)';

  return (
    <>
      {/* 4-sided handles for FloatingEdge */}
      <Handle id="target-top"    type="target" position={Position.Top}    style={{ opacity: 0 }} />
      <Handle id="target-bottom" type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle id="target-left"   type="target" position={Position.Left}   style={{ opacity: 0 }} />
      <Handle id="target-right"  type="target" position={Position.Right}  style={{ opacity: 0 }} />
      <Handle id="source-top"    type="source" position={Position.Top}    style={{ opacity: 0 }} />
      <Handle id="source-bottom" type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle id="source-left"   type="source" position={Position.Left}   style={{ opacity: 0 }} />
      <Handle id="source-right"  type="source" position={Position.Right}  style={{ opacity: 0 }} />

      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          minWidth: '150px',
          maxWidth: '240px',
          padding: '0.625rem 0.75rem',
          backgroundColor: 'var(--cds-layer-01)',
          border: `${selected ? 2 : 1}px solid ${borderColor}`,
          borderLeft: `3px solid var(--cds-support-teal-60, #007d79)`,
          borderRadius: '4px',
          boxShadow,
          transition: 'all 0.15s ease',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
          {/* Icon */}
          <div
            style={{
              width: '22px',
              height: '22px',
              borderRadius: '50%',
              backgroundColor: 'var(--cds-support-teal-60, #007d79)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <FlowData size={12} style={{ fill: 'white' }} />
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--cds-text-primary)',
                lineHeight: 1.3,
                wordBreak: 'break-word',
                marginBottom: (attrCount > 0 || confidence != null) ? '0.375rem' : 0,
              }}
            >
              {d.label}
            </div>

            {/* Badges */}
            {(attrCount > 0 || confidence != null) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {attrCount > 0 && (
                  <Tag size="sm" type="teal">
                    {attrCount} attr{attrCount !== 1 ? 's' : ''}
                  </Tag>
                )}
                {confidence != null && (
                  <Tag
                    size="sm"
                    type={confidence >= 75 ? 'green' : confidence >= 50 ? 'warm-gray' : 'gray'}
                  >
                    {confidence}%
                  </Tag>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export const ConceptInstanceNode = memo(ConceptInstanceNodeInner);

// Made with Bob
